<template>
  <div id="project-tracker-table">
    <k-table v-if="projectsReady && projects.length > 0"
      :headers="kableHeaders"
      :rows="kableRows"
      :hideable="false"
      :panel="false"
      :max="10"
      @clicked="handleTableAction"
    ></k-table>
  </div>
</template>
<script>
import KTable from '@base-components/tables/display/k-table.vue';

export default {
  components: {
    KTable,
  },

  props: {
    projects: {
      type: Array,
    },
    projectsReady: {
      type: Boolean,
      default: false,
    },
    openFeedbackModal: {
      type: Function,
    },
    openEvidencePage: {
      type: Function,
    },
  },

  computed: {
    programmeId() {
      return Number(this.$route.params.programmeId);
    },

    kableHeaders() {
      return {
        phase: {
          name: 'Phase',
          type: 'function',
          filterable: true,
          sortable: true,
          options: {
            callback: this.phaseDisplay,
          },
        },
        name: {
          name: 'Project Name',
          type: 'string',
          filterable: true,
          sortable: true,
        },
        description: {
          name: 'Project Description',
          type: 'string',
          filterable: true,
          sortable: true,
        },
        competencies: {
          name: 'Competencies',
          type: 'tags',
          filterable: true,
          sortable: true,
        },
        status: {
          name: 'Status',
          type: 'pill',
          filterable: true,
          sortable: true,
          displayAs: status => (status === 'scoping_proposed' ? 'scope_proposed' : status),
          options: {
            type: status => ({
              completed: 'success',
              in_progress: 'primary',
              draft: 'neutral',
              scope_proposed: 'proposed',
            }[status] || 'neutral'),
          },
        },
        all_feedback: {
          name: 'View All Feedback',
          type: 'button',
          options: {
            preset: 'open',
            title: 'View All Feedback',
            ariaLabel: 'View All Feedback',
            disabled: row => row.status === 'draft',
          },
        },
        scope: {
          name: 'Project Scope',
          type: 'button',
          options: {
            preset: row => (row.scoping_completed || row.project_completed ? 'open' : 'edit'),
            title: row => (row.scoping_completed || row.project_completed ? 'Open Scope' : 'Edit Scope'),
            ariaLabel: row => (row.scoping_completed || row.project_completed ? 'Open Scope' : 'Edit Scope'),
          },
        },
        mentor_feedback_document: {
          name: 'Download Scoping Feedback Doc',
          type: 'button',
          options: {
            preset: 'download-file',
            text: row => (row.mentor_feedback_document ? 'Download' : 'No feedback document'),
            title: row => (row.mentor_feedback_document ? 'Download Scoping Feedback Document' : 'No Scoping feedback document'),
            ariaLabel: row => (row.mentor_feedback_document ? 'Download Scoping Feedback Document' : 'No Scoping feedback document'),
            disabled: row => !row.mentor_feedback_document,
            downloadUrl: row => this.scopingFeedbackDownloadUrl(row),
          },
        },
        evidence: {
          name: 'Project Evidence',
          type: 'button',
          options: {
            preset: row => (row.status === 'completed' ? 'open' : 'edit'),
            title: row => (row.status === 'completed' ? 'Open Evidence' : 'Edit Evidence'),
            ariaLabel: row => (row.status === 'completed' ? 'Open Evidence' : 'Edit Evidence'),
            disabled: row => (row.status === 'draft' || !row.scoping_completed),
          },
        },
      };
    },

    kableRows() {
      return this.projects.map(project => ({
        id: project.id,
        phase: project.status,
        status: project.status,
        name: project.name,
        description: project.description,
        scoping_completed: project.scoping_completed,
        project_completed: project.project_completed,
        competencies: {
          tags: project.competencies ? project.competencies.map(c => c.label) : [],
          sortBy: project.competencies ? project.competencies.map(c => c.label).join(',') : '',
        },
        scoping_document_link: project.scoping_document_link,
        mentor_feedback_document: project.feedback_document_link,
      }));
    },
  },

  methods: {

    phaseDisplay(row) {
      if (row.phase === 'in_progress' && row.scoping_completed) {
        return 'Creation';
      }
      if (row.phase === 'scoping_proposed' && !row.scoping_completed) {
        return 'Scoping';
      }
      if (row.phase === 'completed') {
        return 'Completed';
      }
      return 'Draft';
    },

    handleTableAction(key, index, row, column) {
      if (column === 'all_feedback') {
        this.$emit('view-all-feedback', row.id);
      } else if (column === 'scope') {
        this.$emit('edit-project', row.id);
      } else if (column === 'evidence') {
        this.openEvidencePage(row.id);
      }
    },

    scopingFeedbackDownloadUrl(row) {
      if (!row.mentor_feedback_document) return '';
      const filename = row.mentor_feedback_document.split('/').pop();
      return `/api/curriculum/programmes/projects/${row.id}/scoping-feedback/${filename}`;
    },
  },
};
</script>
